import type { RichEmbed as RichEmbedData } from '@comms/shared';
import { Markdown } from './Markdown';

const safeHref = (url: string | null) => (url && /^https?:\/\//i.test(url) ? url : undefined);
const hex = (color: number) => `#${color.toString(16).padStart(6, '0')}`;

function ExternalLink({
  href,
  className,
  children,
}: {
  href: string | null;
  className?: string;
  children: React.ReactNode;
}) {
  const safe = safeHref(href);
  if (!safe) return <span className={className}>{children}</span>;
  return (
    <a
      href={safe}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={`${className ?? ''} hover:underline`}
    >
      {children}
    </a>
  );
}

/** A card posted by a bot or webhook. Images are re-hosted by the server. */
export function RichEmbed({ embed, guildId }: { embed: RichEmbedData; guildId?: string | null }) {
  const inline = embed.fields.some((f) => f.inline);
  return (
    <article
      data-embed="rich"
      className="mt-1 grid max-w-[min(32rem,100%)] grid-cols-[minmax(0,1fr)_auto] gap-x-4 rounded border-l-4 bg-bg-1 p-3 text-sm"
      style={{ borderLeftColor: embed.color !== null ? hex(embed.color) : 'var(--color-accent)' }}
    >
      <div className="min-w-0 space-y-1">
        {embed.author && (
          <div className="flex items-center gap-2 text-xs font-semibold text-text-0">
            {embed.author.iconUrl && (
              <img src={embed.author.iconUrl} alt="" className="h-6 w-6 rounded-full" />
            )}
            <ExternalLink href={embed.author.url}>{embed.author.name}</ExternalLink>
          </div>
        )}
        {embed.title && (
          <div className="font-semibold text-text-0">
            <ExternalLink href={embed.url} className={embed.url ? 'text-[#00a8fc]' : undefined}>
              {embed.title}
            </ExternalLink>
          </div>
        )}
        {embed.description && (
          <div className="text-text-1">
            <Markdown content={embed.description} guildId={guildId} />
          </div>
        )}
        {embed.fields.length > 0 && (
          <dl className={inline ? 'grid grid-cols-1 gap-2 pt-1 sm:grid-cols-3' : 'grid gap-2 pt-1'}>
            {embed.fields.map((f, i) => (
              <div key={i} className={f.inline ? 'min-w-0' : 'min-w-0 sm:col-span-3'}>
                <dt className="text-xs font-semibold text-text-0">{f.name}</dt>
                <dd className="text-text-1">
                  <Markdown content={f.value} guildId={guildId} />
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      {embed.thumbnail && (
        <a
          href={embed.thumbnail.url}
          target="_blank"
          rel="noopener noreferrer"
          className="row-span-2"
        >
          <img
            src={embed.thumbnail.url}
            alt=""
            className="max-h-20 max-w-20 rounded object-contain"
            style={{ aspectRatio: `${embed.thumbnail.width} / ${embed.thumbnail.height}` }}
          />
        </a>
      )}
      {embed.image && (
        <a
          href={embed.image.url}
          target="_blank"
          rel="noopener noreferrer"
          className="col-span-2 mt-2 block w-fit"
        >
          <img
            src={embed.image.url}
            alt=""
            loading="lazy"
            className="max-h-80 max-w-full rounded"
            style={{ aspectRatio: `${embed.image.width} / ${embed.image.height}` }}
          />
        </a>
      )}
      {(embed.footer || embed.timestamp) && (
        <div className="col-span-2 mt-2 flex items-center gap-2 text-xs text-text-3">
          {embed.footer?.iconUrl && (
            <img src={embed.footer.iconUrl} alt="" className="h-5 w-5 rounded-full" />
          )}
          {embed.footer && <span>{embed.footer.text}</span>}
          {embed.footer && embed.timestamp && <span aria-hidden>•</span>}
          {embed.timestamp && (
            <time dateTime={embed.timestamp}>
              {new Date(embed.timestamp).toLocaleString([], {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </time>
          )}
        </div>
      )}
    </article>
  );
}
